/**
 * New Homepage Redesign - Single Page Experience
 *
 * DEVELOPMENT LOG: Phase 1 - Foundation & Feature Flags
 * Created: Basic structure for new homepage design
 * Status: Foundation complete, ready for Phase 2 enhancements
 *
 * PHASES PLANNED:
 * Phase 1: ✅ Basic structure and feature flag integration
 * Phase 2: 🔄 Enhanced navigation and user account
 * Phase 3: 🔄 Dynamic content and educational system
 * Phase 4: 🔄 Model selection interface
 * Phase 5: 🔄 Enhanced prompt input and features
 * Phase 6: 🔄 Usage tracking and paywall system
 * Phase 7: 🔄 Mobile optimization and polish
 */

import React, { useState, useEffect } from 'react';
import { MainLayout } from '../../layouts';
import { DebugLabel } from '../shared';
import { DynamicHeadline } from './DynamicHeadline';
import { EducationSection } from './EducationSection';
import { useMetaTags } from '../../hooks/useMetaTags';

// Model group images array
const MODEL_IMAGES = [
  '/images/Model-Group.jpg',
  '/images/Model-Group2.jpg',
  '/images/Model-Group3.jpg',
  '/images/Model-Group4.jpg',
];

export const NewHomePage: React.FC = () => {
  // Cycle through images on each page visit
  const [currentImage, setCurrentImage] = useState<string>('');

  useEffect(() => {
    // Get the last image index from sessionStorage
    const lastIndex = sessionStorage.getItem('sp_model_image_index');
    const nextIndex = lastIndex ? (parseInt(lastIndex, 10) + 1) % MODEL_IMAGES.length : 0;

    // Set the new image
    setCurrentImage(MODEL_IMAGES[nextIndex]);

    // Store the new index
    sessionStorage.setItem('sp_model_image_index', nextIndex.toString());
  }, []);

  // Set meta tags for social sharing
  useMetaTags({
    title: 'Smart Prompt: One Trusted Answer from Many AIs',
    description: 'Compare responses from Claude, GPT-4, Gemini, and more AI models side-by-side. Find the best AI for your needs with Smart Prompt.',
    url: window.location.origin,
    keywords: 'AI comparison, LLM comparison, Claude, GPT-4, Gemini, AI models, prompt testing',
  });

  return (
    <MainLayout>
      <DebugLabel label="NewHomePage-Container" type="page" position="top-left" />

      {/* PHASE 1: Basic Hero Section */}
      <section className="py-16 lg:py-24 relative">
        <DebugLabel label="NewHomePage-HeroSection" type="section" position="top-right" />
        <div className="max-w-6xl mx-auto px-6">

          {/* PHASE 3: Dynamic Headlines */}
          <DynamicHeadline />

          {/* Model Group Image */}
          <div className="max-w-4xl mx-auto mt-12">
            {currentImage && (
              <img
                src={currentImage}
                alt="AI Model Comparison Group"
                className="w-full h-auto rounded-2xl"
              />
            )}
          </div>
        </div>
      </section>

      {/* PHASE 3: Enhanced Educational Sections */}
      <EducationSection />

      {/* Development Status Indicator */}
      {import.meta.env.DEV && (
        <div className="fixed bottom-4 right-4 bg-slate-800 border border-slate-600 rounded-lg p-3 text-xs text-slate-300 max-w-xs">
          <div className="font-semibold text-cyan-400 mb-1">🎉 HOMEPAGE REDESIGN - 98% COMPLETE</div>
          <div>Phase 1: ✅ Foundation & Feature Flags</div>
          <div>Phase 2: ✅ Enhanced Navigation & User Account</div>
          <div>Phase 3: ✅ Dynamic Content System</div>
          <div>Phase 4: ✅ Model Selection Interface</div>
          <div>Phase 5: ✅ Enhanced Prompt Input & Features</div>
          <div>Phase 6: ✅ Usage Tracking & Paywall System</div>
          <div>Phase 7: ✅ Mobile Optimization & Polish</div>
          <div className="mt-2 pt-2 border-t border-slate-600 text-green-400 font-semibold">
            🚀 PRODUCTION READY!
          </div>
        </div>
      )}
    </MainLayout>
  );
};